import { StatusCode } from "api/protocols";
import { GetProduct } from "api/rpc/2024-04/facilityAdmin/product/product";
import { GetProductTypes } from "api/rpc/2024-04/facilityAdmin/product/type";
import axios, { CancelToken } from "axios";
import classNames from "classnames";
import Checkbox from "components/form/checkbox/Checkbox";
import FormLayout from "components/form/FormLayout";
import Input from "components/form/input";
import Sheet from "components/sheet/Sheet";
import Portal from "elements/Portal";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import useCurrencyFacility from "hooks/useCurrency/useCurrencyFacility";
import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import { showError } from "redux/actions/ui";
import { IReward } from "redux/reducers/models/loyalty";
import { ICategories, IDepartments, IProduct, TProductType } from "redux/reducers/models/product";
import { Select } from "components/select/index";
import "../loyalty.scss";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp, faExclamationCircle, faXmark } from "@fortawesome/pro-regular-svg-icons";
import AddFolderProductsModal from "components/folders/AddFolderProductsModal";
import { IProductPickerState } from "../../teesheets/pricingSheets/pricingSheetApplications";
import { defaultBrowseFilter, IBrowseFilter } from "components/folders/productBrowseFilters";
import { GetDepartments } from "api/rpc/2024-04/facilityAdmin/facility/department";
import { useHistory } from "react-router";
import { appendUniqueProducts, buildSelectAllProductBatch, fetchFolderProductSearchPage, IFolderProductSearchContext } from "components/folders/folderProductSearch";
import { ButtonNew as Button } from "components/buttonNew";

interface IProps {
    open: boolean;
    onCancel: () => void;
    onOk: (offer: Partial<IReward & { product_type_ids?: Array<number>; product_ids?: Array<number> }>) => void;
    reward?: IReward & { products?: Array<IProduct>; product_types?: Array<TProductType> };
}

interface IState {
    id?: number;
    name: string;
    reward_type: "discount_percent" | "discount_amount" | "free_product";
    application: "all_products" | "product_categories" | "product" | "order";
    discount_value?: string;
    points_required?: string;
}

const ITEMS_SELECT_ALL_LIMIT = 200;

type TBrowseFilterState = {
    search: string;
    browseFilter: IBrowseFilter;
};

type TBrowseDepartment = IDepartments & { cateogries?: ICategories[] };


export default function LoyaltyProgramRewardsModal(props: IProps) {
    const { open, onCancel, onOk, reward } = props;
    const { t } = useTranslation();
    const history = useHistory();
    const dispatch = useAppDispatch();
    const { getCurrencySymbol } = useCurrencyFacility();
    const [types, setTypes] = useState<Array<TProductType>>([]);
    const [selectedTypes, setSelectedTypes] = useState<Array<TProductType & { name?: string }>>([]);
    const [typeSearch, setTypeSearch] = useState<string>("");
    const [selectedProducts, setSelectedProducts] = useState<Array<IProduct & { name?: string }>>([]);
    const { facilityStore } = useAppSelector(store => store);
    const [state, setState] = useState<IState>({
        id: null,
        name: "",
        reward_type: "discount_percent",
        application: "product",
        discount_value: "",
        points_required: "",
    });
    const [productState, setProductState] = useState<IProductPickerState>({
        addProductVisibility: false,
        selectedProducts: [],
        addedVariants: [],
        productSearchResults: undefined,
        productSearchTotalCount: undefined,
        productSearchPending: false,
        productSearchLoadingMore: false,
        productSearchSelectAllPending: false,
    });
    const [productTagsVisible, setProductTagsVisible] = useState<boolean>(false);
    const [hideProductTags, setHideProductTags] = useState<boolean>(false);
    const [viewProducts, setViewProducts] = useState<boolean>(false);
    const [browseFilterState, setBrowseFilterState] = useState<TBrowseFilterState>({
        search: "",
        browseFilter: defaultBrowseFilter,
    });
    const [browseDepartments, setBrowseDepartments] = useState<TBrowseDepartment[]>([]);
    const [browseMetadataLoading, setBrowseMetadataLoading] = useState(false);
    const [uncategorizedCount, setUncategorizedCount] = useState(0);
    const excludedVariantIds = useMemo(
        () => productState.addedVariants.map(variant => variant.id).filter((id): id is number => id != null),
        [productState.addedVariants],
    );
    const productSearchCancelTokenRef = useRef<ReturnType<typeof axios.CancelToken.source> | null>(null);

    const excludedVariantIdSet = useMemo(() => new Set(excludedVariantIds), [excludedVariantIds]);
    const browseFilterRef = useRef(browseFilterState.browseFilter);
    const prevAddProductsModalOpen = useRef(false);
    const { Option } = Select;

    useEffect(() => {
        const source = axios.CancelToken.source();
        void getProductTypes(source.token);
        return () => source.cancel();
    }, []);

    useEffect(() => {
        if (selectedProducts && selectedProducts?.length > 10) {
            ReactDOM.unstable_batchedUpdates(() => {
                setProductTagsVisible(false);
                setHideProductTags(true);
            });
        } else {
            setHideProductTags(false);
        }
    }, [selectedProducts]);

    useEffect(() => {
        if (!open) {
            ReactDOM.unstable_batchedUpdates(() => {
                setState({
                    id: null,
                    name: "",
                    reward_type: "discount_percent",
                    application: "product",
                    discount_value: "",
                    points_required: "",
                });
                setSelectedTypes([]);
                setSelectedProducts([]);
                setTypeSearch("");
                setProductTagsVisible(false);
            });
        } else {
            if (reward) {
                setState({
                    ...reward,
                    discount_value: reward?.discount_value?.toString(),
                    points_required: reward?.points_required?.toString(),
                });
                if (reward?.application === "product" && reward?.products?.length > 0) {
                    setSelectedProducts([...reward.products]?.map(product => ({ ...product, name: product?.preferred_title ? product?.preferred_title : product?.title })));
                } else if (reward?.application === "product_categories" && reward?.product_types?.length > 0) {
                    setSelectedTypes([...reward.product_types]?.map(type => ({ ...type, name: type?.title })));
                }
            }
        }
    }, [open]);

    useEffect(() => {
        if (!productState.addProductVisibility) {
            productSearchCancelTokenRef.current?.cancel();
            setProductState(prevState => ({
                ...prevState,
                productSearchPending: false,
                productSearchLoadingMore: false,
            }));
            prevAddProductsModalOpen.current = false;
            return;
        }

        const justOpened = !prevAddProductsModalOpen.current;
        prevAddProductsModalOpen.current = true;

        const browseChanged =
            browseFilterRef.current.type !== browseFilterState.browseFilter.type ||
            browseFilterRef.current.id !== browseFilterState.browseFilter.id;
        browseFilterRef.current = browseFilterState.browseFilter;
        const delay = browseChanged ? 0 : 1000;

        let mounted = true;
        productSearchCancelTokenRef.current?.cancel();
        const cancelSource = axios.CancelToken.source();
        productSearchCancelTokenRef.current = cancelSource;

        setProductState(prevState => {
            const hasLoadedResults = (prevState.productSearchResults?.length ?? 0) > 0;

            return {
                ...prevState,
                productSearchPending: true,
                productSearchLoadingMore: false,
                ...(hasLoadedResults ? {} : { productSearchResults: undefined, productSearchTotalCount: undefined }),
            };
        });

        const runFetch = async () => {
            try {
                const { products, totalCount } = await fetchFolderProductSearchPage(
                    GetProduct,
                    getProductSearchContext(),
                    0,
                    true,
                    cancelSource.token,
                    () => dispatch(showError(t("Failed to get products"))),
                );
                if (mounted) {
                    setProductState(prevState => ({
                        ...prevState,
                        productSearchResults: products,
                        productSearchTotalCount: totalCount,
                        productSearchPending: false,
                        productSearchLoadingMore: false,
                    }));
                }
            } catch (error) {
                if (axios.isCancel(error)) {
                    return;
                }
                if (mounted) {
                    setProductState(prevState => ({
                        ...prevState,
                        productSearchResults: [],
                        productSearchTotalCount: 0,
                        productSearchPending: false,
                        productSearchLoadingMore: false,
                    }));
                }
            }
        };

        let timeoutId: ReturnType<typeof setTimeout> | undefined;
        if (justOpened) {
            void runFetch();
        } else {
            timeoutId = global.setTimeout(() => {
                void runFetch();
            }, delay);
        }

        return () => {
            mounted = false;
            cancelSource.cancel();
            if (timeoutId !== undefined) {
                clearTimeout(timeoutId);
            }
        };
    }, [browseFilterState.search, browseFilterState.browseFilter, productState.addProductVisibility]);

    function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
        const { id, value } = e.target;

        setState(prev => ({ ...prev, [id]: value }));
    }
    async function getProductTypes(cancelToken?: CancelToken) {
        const res = await GetProductTypes(null, false, cancelToken);
        if (res?.status !== StatusCode.OK) {
            if (cancelToken && cancelToken?.reason) {
                return;
            }
            dispatch(showError(t("Error getting product types")));
            return;
        }
        setTypes(res?.data);
    }

    function handleOk(reward: Partial<IReward & {
        product_type_ids?: number[];
        product_ids?: number[];
    }>) {
        if (!state?.name) {
            dispatch(showError(t("Reward name required")));
            return;
        } else if (state?.reward_type === "discount_percent" && Math.abs(Number(state?.discount_value)) > 100) {
            dispatch(showError(t("Discount percent cannot be over 100")));
            return;
        }

        void onOk(reward);
    }

    async function loadBrowseMetadata() {
        const showLoading = browseDepartments.length === 0;

        if (showLoading) {
            setBrowseMetadataLoading(true);
        }

        try {
            const [departmentRes, countRes] = await Promise.all([
                GetDepartments({ extended: true }, false),
                GetProduct(
                    {
                        get_count: true,
                        limit: 1,
                        filters: [{ label: "uncategorized", value: [1] }],
                    },
                    false,
                ),
            ]);

            if (departmentRes.status === StatusCode.OK) {
                setBrowseDepartments((departmentRes.data ?? []).filter(department => department.type === "department"));
            }

            if (countRes.status === StatusCode.OK && countRes.data && !Array.isArray(countRes.data)) {
                const countData = countRes.data as { count?: number };
                setUncategorizedCount(countData.count ?? 0);
            }
        } finally {
            if (showLoading) {
                setBrowseMetadataLoading(false);
            }
        }
    }

    function handleBrowseProductsVisibility(selectProducts: boolean = true) {
        const opening = !productState.addProductVisibility;

        setProductState(prevState => ({
            ...prevState,
            addProductVisibility: opening,
            selectedProducts: [...selectedProducts],
            ...(opening
                ? {
                    productSearchPending: true,
                    productSearchResults: undefined,
                    productSearchTotalCount: undefined,
                }
                : {}),
        }));

        if (opening) {
            ReactDOM.unstable_batchedUpdates(() => {
                setViewProducts(selectProducts);
                setBrowseFilterState({ search: "", browseFilter: defaultBrowseFilter });
            });

            void loadBrowseMetadata();
        } else {
            ReactDOM.unstable_batchedUpdates(() => {
                setViewProducts(false);
                setBrowseFilterState({ search: "", browseFilter: defaultBrowseFilter });
            });
        }
    }

    function handleAddProductsBrowseFilterChange(browseFilter: IBrowseFilter) {
        setBrowseFilterState(prevState => ({ ...prevState, browseFilter }));
        setProductState(prev => ({ ...prev, productSearchPending: true }));
    }

    async function loadMoreProducts() {
        let shouldLoad = false;
        let offset = 0;

        setProductState(prev => {
            if (
                prev.productSearchLoadingMore ||
                prev.productSearchPending ||
                !prev.productSearchResults ||
                prev.productSearchTotalCount == null ||
                prev.productSearchResults.length >= prev.productSearchTotalCount
            ) {
                return prev;
            }

            shouldLoad = true;
            offset = prev.productSearchResults.length;
            return { ...prev, productSearchLoadingMore: true };
        });

        if (!shouldLoad) {
            return;
        }

        try {
            const { products } = await fetchFolderProductSearchPage(
                GetProduct,
                getProductSearchContext(),
                offset,
                false,
                undefined,
                () => dispatch(showError(t("Failed to get products"))),
            );
            setProductState(prev => ({
                ...prev,
                productSearchResults: appendUniqueProducts(prev.productSearchResults ?? [], products),
                productSearchLoadingMore: false,
            }));
        } catch {
            setProductState(prev => ({ ...prev, productSearchLoadingMore: false }));
        }
    }

    function getProductSearchContext(): IFolderProductSearchContext {
        return {
            search: browseFilterState.search,
            browseFilter: browseFilterState.browseFilter,
            variants: true,
        };
    }

    function handleAddProductsBrowseSearchChange(value: string) {
        setBrowseFilterState(prevState => ({ ...prevState, search: value }));
        setProductState(prev => ({ ...prev, productSearchPending: true }));
    }

    async function selectAllMatchingProducts() {
        const loaded = productState.productSearchResults ?? [];
        const totalCount = productState.productSearchTotalCount ?? loaded.length;
        const fullyAddedIds = new Set(
            loaded.filter(product => withSelectableVariants(product) == null).map(product => product.id),
        );
        const fullySelectedIds = new Set(
            productState.selectedProducts
                .filter(selected => {
                    const source = loaded.find(product => product.id === selected.id) ?? selected;
                    const available = withSelectableVariants(source);
                    if (available == null) {
                        return true;
                    }

                    const selectedVariantIds = new Set((selected.variants ?? []).map(variant => variant.id));
                    return available.variants.every(variant => selectedVariantIds.has(variant.id));
                })
                .map(product => product.id),
        );
        const excludeIds = new Set<number>([...fullyAddedIds, ...fullySelectedIds]);
        const remaining = Math.max(0, ITEMS_SELECT_ALL_LIMIT - productState.selectedProducts.length);
        const needsRemoteFetch = remaining > 0 && loaded.length < totalCount;

        if (needsRemoteFetch) {
            setProductState(prev => ({ ...prev, productSearchSelectAllPending: true }));
        }

        try {
            const toAdd = await buildSelectAllProductBatch(
                GetProduct,
                getProductSearchContext(),
                loaded,
                totalCount,
                excludeIds,
                remaining,
                () => dispatch(showError(t("Failed to get products"))),
            );

            const selectable = toAdd
                .map(product => withSelectableVariants(product))
                .filter((product): product is IProduct => product != null);

            setProductState(prev => {
                if (selectable.length === 0) {
                    return { ...prev, productSearchSelectAllPending: false };
                }

                return {
                    ...prev,
                    selectedProducts: mergeSelectedProducts(prev.selectedProducts, selectable),
                    productSearchSelectAllPending: false,
                };
            });
        } catch {
            setProductState(prev => ({ ...prev, productSearchSelectAllPending: false }));
        }
    }

    function mergeSelectedProducts(existing: IProduct[], incoming: IProduct[]) {
        const next = [...existing];

        incoming.forEach(product => {
            const index = next.findIndex(selected => selected.id === product.id);
            if (index === -1) {
                next.push(product);
                return;
            }

            const existingVariantIds = new Set((next[index].variants ?? []).map(variant => variant.id));
            const mergedVariants = [...(next[index].variants ?? [])];
            (product.variants ?? []).forEach(variant => {
                if (!existingVariantIds.has(variant.id)) {
                    mergedVariants.push(variant);
                    existingVariantIds.add(variant.id);
                }
            });
            next[index] = { ...next[index], variants: mergedVariants, variant_count: mergedVariants.length };
        });

        return next;
    }

    function withSelectableVariants(product: IProduct): IProduct | null {
        const variants = (product.variants ?? []).filter(variant => !excludedVariantIdSet.has(variant.id));
        if (variants.length === 0) {
            return null;
        }

        return {
            ...product,
            variants,
            variant_count: variants.length,
        };
    }

    function handleSelectProductToAdd(productOrProducts: IProduct | IProduct[]) {
        const products = Array.isArray(productOrProducts) ? productOrProducts : [productOrProducts];
        const selectable = products
            .map(product => withSelectableVariants(product))
            .filter((product): product is IProduct => product != null);

        if (selectable.length === 0) {
            return;
        }

        setProductState(prev => ({
            ...prev,
            selectedProducts: mergeSelectedProducts(prev.selectedProducts, selectable),
        }));
    }

    function handleUnselectProductToAdd(productOrProducts: IProduct | IProduct[]) {
        const products = Array.isArray(productOrProducts) ? productOrProducts : [productOrProducts];

        setProductState(prev => {
            let next = [...prev.selectedProducts];

            products.forEach(incoming => {
                const index = next.findIndex(product => product.id === incoming.id);
                if (index === -1) {
                    return;
                }

                const removeIds = new Set((incoming.variants ?? []).map(variant => variant.id));
                if (removeIds.size === 0) {
                    next = next.filter(product => product.id !== incoming.id);
                    return;
                }

                const remaining = (next[index].variants ?? []).filter(variant => !removeIds.has(variant.id));
                if (remaining.length === 0) {
                    next = next.filter(product => product.id !== incoming.id);
                } else {
                    next[index] = { ...next[index], variants: remaining, variant_count: remaining.length };
                }
            });

            return { ...prev, selectedProducts: next };
        });
    }

    return (
        <>
            <Portal isMounted={open}>
                <Sheet
                    open={open}
                    size="small"
                    title={t("Reward")}
                    supportText={t("Choose what customers can redeem their points for")}
                    closable
                    onCancel={onCancel}
                    onOk={() => handleOk({
                        ...state,
                        discount_value: Math.abs(Number(state?.discount_value)),
                        points_required: Math.round(Math.abs(Number(state?.points_required))),
                        product_ids: state?.application === "product" ? selectedProducts?.map(product => product?.id) : undefined,
                        product_type_ids: state?.application === "product_categories" ? selectedTypes?.map(type => type?.id) : undefined,
                    })}
                    okText={t("Save")}
                // overflow
                >
                    <FormLayout>
                        <FormLayout.Group>
                            <p className="loyalty-checkbox-title">{t("Select the Reward Type")}:</p>
                        </FormLayout.Group>
                        <FormLayout.Group>
                            <div className={classNames("loyalty-checkbox-container", { "loyalty-checkbox-container-active": state?.reward_type === "discount_percent" })} onClick={() => setState(prev => ({ ...prev, reward_type: "discount_percent" }))}>
                                <Checkbox size="small" circle labelHidden checked={state?.reward_type === "discount_percent"} onChange={() => { }} />
                                <p>{t("Discount (%)")}</p>
                            </div>
                            <div className={classNames("loyalty-checkbox-container", { "loyalty-checkbox-container-active": state?.reward_type === "discount_amount" })} onClick={() => setState(prev => ({ ...prev, reward_type: "discount_amount" }))}>
                                <Checkbox size="small" circle labelHidden checked={state?.reward_type === "discount_amount"} onChange={() => { }} />
                                <p>{t("Discount")} ({getCurrencySymbol(facilityStore?.facility?.default_currency_code)})</p>
                            </div>
                            <div className={classNames("loyalty-checkbox-container", { "loyalty-checkbox-container-active": state?.reward_type === "free_product" })} onClick={() => setState(prev => ({ ...prev, reward_type: "free_product", discount_value: "100", application: prev?.application === "order" ? "product" : prev?.application }))}>
                                <Checkbox size="small" circle labelHidden checked={state?.reward_type === "free_product"} onChange={() => { }} />
                                <p>{t("Free Product")}</p>
                            </div>
                        </FormLayout.Group>
                        <FormLayout.Group>
                            <Input label={t("Reward Name")} onChange={handleInputChange} value={state?.name} id="name" placeholder={t("Reward Name")} />
                        </FormLayout.Group>

                        <FormLayout.Group>
                            <Select
                                onChange={val => setState(prev => ({ ...prev, application: val }))}
                                placeholder={t("Select Application")}
                                allowClear
                                showDropDownOnFocus={true}
                                usePortal
                                defaultValue={state?.application}
                                label={t("Apply To")}
                            >
                                {[
                                    // {id: "all_products", label: `${t("All Products")}`}, 
                                    { id: "product_categories", label: `${t("Specific Product Type")}` },
                                    { id: "product", label: `${t("Specific Product")}` },
                                    { id: "order", label: `${t("Order Subtotal")}` }]?.filter(option => state?.reward_type === "free_product" ? option?.id !== "order" : true)?.map((option, index) => {
                                        return (
                                            <Option key={index} value={option?.id} extraValues={option}>
                                                {option?.label}
                                            </Option>
                                        );
                                    })}
                            </Select>


                        </FormLayout.Group>
                    </FormLayout>

                    {state?.application === "product_categories" ? (<>
                        <FormLayout>
                            <FormLayout.Group>
                                <Select
                                    showSearch
                                    onSearch={(query: string) => setTypeSearch(query)}
                                    onChange={(_, extraValue: TProductType & { name?: string; }) => setSelectedTypes(prev => ([...prev, extraValue]))}
                                    allowClear
                                    searchValue={typeSearch}
                                    searching={!types}
                                    showDropDownOnFocus
                                    usePortal
                                    placeholder={t("Search product types")}
                                    noData={
                                        <div className="flex items-center p-2 text-black">
                                            <FontAwesomeIcon icon={faExclamationCircle} fixedWidth size="1x" />
                                            <span className="ml-3 italic">{t("No product type found...")}</span>
                                        </div>
                                    }
                                >
                                    {types?.filter(type => !selectedTypes?.some(selectedType => selectedType?.id === type?.id) && type?.title?.toLocaleLowerCase()?.includes(typeSearch?.toLocaleLowerCase()))?.map((type, index) => {
                                        return (
                                            <Option key={index} value={type?.id} extraValues={type}>
                                                <p>{type?.title}</p>
                                            </Option>
                                        );
                                    })}
                                </Select>
                            </FormLayout.Group>
                            <FormLayout.Group>
                                {/* Selected product category tags */}
                                <div className="loyalty-tags-container">
                                    {selectedTypes?.map((type, index) => {
                                        return (
                                            <div key={index} className="loyalty-tag">
                                                <p className="loyalty-tag-text">{type?.title}</p>
                                                <FontAwesomeIcon icon={faXmark} className="loyalty-tag-x" onClick={() => setSelectedTypes(prev => prev?.filter(prevType => prevType?.id !== type?.id))} />
                                            </div>
                                        );
                                    })}
                                </div>
                            </FormLayout.Group>
                        </FormLayout>
                    </>
                    ) :

                        state?.application === "product" ? <>
                            <div className="loyalty-products-container">
                                <div className={classNames("loyalty-products-container-title-container", { "loyalty-products-container-title-container-hide-divider": hideProductTags })}><p className="loyalty-products-container-title">{t("Included products")} <span className="loyalty-products-container-quantity">{`(${selectedProducts?.length})`}</span></p>
                                    <div className="flex items-center gap-4">
                                        <Button type="text" size="small" onClick={() => handleBrowseProductsVisibility(true)}>{selectedProducts?.length === 0 ? t("Add") : t("Edit")}</Button>
                                        {hideProductTags ? (<div><Button size="small" type="secondary" onClick={() => handleBrowseProductsVisibility(false)}>{t("View products")}</Button></div>) : null}
                                        {selectedProducts?.length > 0 && !hideProductTags ? (
                                            <FontAwesomeIcon className="cursor-pointer" icon={productTagsVisible ? faChevronUp : faChevronDown} onClick={() => setProductTagsVisible(prev => !prev)} />
                                        ) : null}
                                    </div>
                                </div>
                                {productTagsVisible ? (
                                    <div className="loyalty-tags-container">
                                        {selectedProducts?.map((product, index, products) => {
                                            return (
                                                <div key={index} className="loyalty-tag">
                                                    <p className="loyalty-tag-text">{product?.preferred_title ? product?.preferred_title : product?.title} {product?.subtitle ? ` • ${product?.subtitle}` : null}</p>
                                                    <FontAwesomeIcon icon={faXmark} className="loyalty-tag-x" onClick={() => {
                                                        ReactDOM.unstable_batchedUpdates(() => {
                                                            if (products?.length === 1) {
                                                                setProductTagsVisible(false);
                                                            }
                                                            setSelectedProducts(prev => prev?.filter(prevProduct => prevProduct?.id !== product?.id));
                                                        });
                                                    }} />
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : null}
                            </div>
                        </> : null}

                    <FormLayout>
                        <FormLayout.Group>
                            <Input
                                prefix={state?.reward_type === "discount_percent" || state?.reward_type === "free_product" ? "%" : getCurrencySymbol(facilityStore?.facility?.default_currency_code)}
                                type="number"
                                label={state?.reward_type === "discount_percent" || state?.reward_type === "free_product" ? t("Discount (%)") : `${t("Discount")} (${getCurrencySymbol(facilityStore?.facility?.default_currency_code)})`}
                                onChange={handleInputChange}
                                value={state?.discount_value}
                                id="discount_value"
                                disabled={state?.reward_type === "free_product"}
                                min="0"
                                placeholder={"10"} />
                            <Input
                                suffix={t("points")}
                                type="number"
                                label={t("Points Amount")}
                                onChange={handleInputChange}
                                value={state?.points_required}
                                id="points_required"
                                min="0"
                                step="1"
                                placeholder={"100"} />
                        </FormLayout.Group>
                    </FormLayout>
                </Sheet>

            </Portal>

            <Portal isMounted={productState?.addProductVisibility}>
                <AddFolderProductsModal
                    open={productState.addProductVisibility}
                    onClose={handleBrowseProductsVisibility}
                    onAdd={products => {
                        setSelectedProducts([...products]);
                        void handleBrowseProductsVisibility();
                    }}
                    search={browseFilterState.search}
                    onSearchChange={handleAddProductsBrowseSearchChange}
                    browseFilter={browseFilterState.browseFilter}
                    onBrowseFilterChange={handleAddProductsBrowseFilterChange}
                    departments={browseDepartments}
                    departmentsLoading={browseMetadataLoading}
                    uncategorizedCount={uncategorizedCount}
                    productsToAdd={productState.selectedProducts}
                    availableSlotCount={ITEMS_SELECT_ALL_LIMIT}
                    totalEmptySlotCount={ITEMS_SELECT_ALL_LIMIT}
                    folderProducts={[]}
                    productSearchResults={productState.productSearchResults}
                    productSearchTotalCount={productState.productSearchTotalCount}
                    productSearchPending={productState.productSearchPending}
                    productSearchLoadingMore={productState.productSearchLoadingMore}
                    productSearchSelectAllPending={productState.productSearchSelectAllPending}
                    onLoadMoreProducts={loadMoreProducts}
                    onSelectAllMatchingProducts={selectAllMatchingProducts}
                    onSelectProduct={handleSelectProductToAdd}
                    onUnselectProduct={handleUnselectProductToAdd}
                    hideSlots
                    excludedVariantIds={excludedVariantIds}
                    title={t("Browse products")}
                    supportText={t("Add items to this purchase order")}
                    onCreateProduct={() => {
                        void handleBrowseProductsVisibility();
                        history.push("/admin/product/new");
                    }}
                    onGoToProducts={() => {
                        void handleBrowseProductsVisibility();
                        history.push("/admin/product");
                    }}
                    browseOnOpen={viewProducts}
                />
            </Portal>
        </>
    );

}